import {
  ActionType,
  RoutineConfig,
  RoutineType,
  RoutineWorkflow,
  useFindByQuery,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useMemo } from 'react';
import { RoutineExecutionProps, RoutineModules } from '../routine';

type IssueEditRoutineModalProps = {
  isEditRoutineLoader: boolean;
  currentTitle: string;
  issue: any;
  onRoutineEdition: (props: RoutineExecutionProps) => void;
  onAlertWorkflowDetected?: () => void;
};

export const IssueEditRoutineLoader = (props: IssueEditRoutineModalProps) => {
  const {
    issue,
    isEditRoutineLoader,
    onRoutineEdition,
    onAlertWorkflowDetected,
  } = props;
  const { workspaceAsset, isLoading: isLoadingWorkspaceAsset } =
    useWorkspaceAsset(issue?.asset?.id);
  const { data: workflowData, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter: { workspaceAsset: { eq: workspaceAsset?.id } } },
  });

  const assetRoutine = useMemo(() => issue?.assetRoutine, [issue]);

  const getRoutineWorkflows = useCallback(
    (id: string, config: RoutineConfig, state: any) => {
      if (!id || !workflowData?.items?.length) return [];

      return workflowData.items
        .filter(
          (item: RoutineWorkflow) =>
            item.trigger?.config?.workspaceRoutineId === id
        )
        .map((workflow: RoutineWorkflow) => {
          return { ...workflow, uom: config?.uom, count: state?.count };
        });
    },
    [workflowData]
  );

  const routine = assetRoutine?.routineVersion;

  const routineState = useMemo(
    () => ({
      workspaceRoutineId: assetRoutine?.routine?.id,
      workspaceRoutineVersionId: assetRoutine?.routineVersion?.id,
      state: assetRoutine?.data,
    }),
    [assetRoutine]
  );

  const workflows = useMemo(
    () =>
      getRoutineWorkflows(
        routineState?.workspaceRoutineId ?? '',
        routine?.config as RoutineConfig,
        routineState
      ),
    [routineState, routine, getRoutineWorkflows]
  );

  useEffect(() => {
    if (
      isEditRoutineLoader &&
      !isLoadingWorkspaceAsset &&
      !isLoading &&
      workspaceAsset &&
      routine &&
      assetRoutine
    ) {
      if (workflows.some((w: any) => w.action?.type === ActionType.Alert)) {
        onAlertWorkflowDetected?.();
        return;
      }
      const module = RoutineModules[routine?.type as RoutineType];
      onRoutineEdition({
        workspaceAsset: workspaceAsset,
        routine,
        module,
        state: routineState,
        workflows,
        isIssueEditRoutine: true,
        assetRoutine,
        sourceIssue: issue,
      });
    }
  }, [
    isEditRoutineLoader,
    isLoadingWorkspaceAsset,
    isLoading,
    workspaceAsset,
    routine,
    routineState,
    workflows,
    onRoutineEdition,
    assetRoutine,
    issue,
  ]);

  return null;
};

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdBy {
          id
        }
      }
    }
}  
`;
